"use client"

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import type { PlatformSession, PlatformSessionUpdate } from "./sessionTypes"

export type PlatformSessionStatus = "loading" | "authenticated" | "unauthenticated"

type PlatformSessionContextValue = {
  data: PlatformSession | null
  status: PlatformSessionStatus
  update: (payload?: PlatformSessionUpdate) => Promise<PlatformSession | null>
}

const PlatformSessionContext = createContext<PlatformSessionContextValue | null>(null)

const fetchSession = async (): Promise<PlatformSession | null> => {
  const response = await fetch("/api/platform-session/current", {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    cache: "no-store",
  })
  if (response.status === 401 || response.status === 204) return null
  if (!response.ok) throw new Error(`Platform session request failed with HTTP ${response.status}`)
  return response.json() as Promise<PlatformSession>
}

export function PlatformSessionProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<PlatformSession | null>(null)
  const [status, setStatus] = useState<PlatformSessionStatus>("loading")

  const refresh = useCallback(async () => {
    try {
      const session = await fetchSession()
      setData(session)
      setStatus(session ? "authenticated" : "unauthenticated")
      return session
    } catch {
      setData(null)
      setStatus("unauthenticated")
      return null
    }
  }, [])

  useEffect(() => {
    const initialRefresh = window.setTimeout(() => { void refresh() }, 0)
    const onFocus = () => { void refresh() }
    window.addEventListener("focus", onFocus)
    return () => {
      window.clearTimeout(initialRefresh)
      window.removeEventListener("focus", onFocus)
    }
  }, [refresh])

  const update = useCallback(async (payload: PlatformSessionUpdate = {}) => {
    const response = await fetch("/api/platform-session/current", {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Turkwise-BFF": "1",
      },
      credentials: "same-origin",
      body: JSON.stringify(payload),
      cache: "no-store",
    })
    if (response.status === 401) {
      setData(null)
      setStatus("unauthenticated")
      return null
    }
    if (!response.ok) throw new Error(`Platform session update failed with HTTP ${response.status}`)
    const session = await response.json() as PlatformSession
    setData(session)
    setStatus("authenticated")
    return session
  }, [])

  const value = useMemo(() => ({ data, status, update }), [data, status, update])
  return <PlatformSessionContext.Provider value={value}>{children}</PlatformSessionContext.Provider>
}

export const useSession = (): PlatformSessionContextValue => {
  const context = useContext(PlatformSessionContext)
  if (!context) throw new Error("useSession must be used within PlatformSessionProvider")
  return context
}

export const getSession = fetchSession

export const startAuth0Login = (options: {
  returnTo?: string | null
  prompt?: string
  loginHint?: string
  screenHint?: "signup"
  connection?: string
} = {}): void => {
  const url = new URL("/auth/login", window.location.origin)
  if (options.returnTo) url.searchParams.set("returnTo", options.returnTo)
  if (options.prompt) url.searchParams.set("prompt", options.prompt)
  if (options.loginHint) url.searchParams.set("login_hint", options.loginHint)
  if (options.screenHint) url.searchParams.set("screen_hint", options.screenHint)
  if (options.connection) url.searchParams.set("connection", options.connection)
  window.location.assign(url.toString())
}

export const startAuth0Signup = (
  returnTo?: string | null,
  mode: "signup" | "login" = "signup",
): void => {
  const url = new URL("/api/platform-signup/start", window.location.origin)
  if (returnTo) url.searchParams.set("returnTo", returnTo)
  if (mode === "login") url.searchParams.set("mode", mode)
  window.location.assign(url.toString())
}

export const startAccountLinkRecovery = (target: "google" | "password"): void => {
  window.location.assign(`/api/platform-account-link/recover?target=${target}`)
}

export const signIn = async (
  provider: "auth0" | "google" | "credentials",
  options: { callbackUrl?: string; email?: string; [key: string]: unknown } = {},
  authorizationParameters: { prompt?: string; login_hint?: string; screen_hint?: "signup" } = {},
): Promise<{ ok: true }> => {
  startAuth0Login({
    returnTo: options.callbackUrl,
    prompt: authorizationParameters.prompt ?? "login",
    loginHint: authorizationParameters.login_hint ?? options.email,
    screenHint: authorizationParameters.screen_hint,
    connection: provider === "google" ? "google-oauth2" : undefined,
  })
  return { ok: true }
}
